import React, { useEffect, useRef, useState } from 'react';
import { Plus, Check } from 'lucide-react';
import { Input } from './ui/input';

/**
 * Autocomplete for Investment Name.
 * - Suggests matching asset names as user types.
 * - If query has no exact match, shows a "+ Create new" option at the bottom.
 * - Calling onSelectAsset(asset) picks an existing asset.
 * - Calling onCreateNew(name) triggers the create-asset flow (parent opens dialog).
 */
export default function InvestmentNameCombobox({
  value,
  assets,
  disabled,
  onChangeText,
  onSelectAsset,
  onCreateNew,
  testIdPrefix = 'investment',
}) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const query = (value || '').trim();
  const lower = query.toLowerCase();

  const matches = query
    ? assets
        .filter((a) => a.investmentName.toLowerCase().includes(lower))
        .slice(0, 7)
    : assets.slice(0, 7);

  const exact = !!assets.find((a) => a.investmentName.toLowerCase() === lower);
  const canCreate = query.length >= 2 && !exact;

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        value={value || ''}
        disabled={disabled}
        onChange={(e) => {
          onChangeText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setFocused(false)}
        placeholder="Start typing…"
        className="h-7 text-xs rounded-sm border-transparent hover:border-border focus:border-[hsl(var(--primary))]"
        data-testid={`${testIdPrefix}-input`}
      />
      {open && (matches.length > 0 || canCreate) && (
        <div
          className="absolute z-40 mt-1 left-0 right-0 min-w-[260px] bg-white border border-border shadow-[0_8px_24px_rgba(0,0,0,0.08)] max-h-[260px] overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {matches.map((a) => {
            const isExact = a.investmentName.toLowerCase() === lower;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => { onSelectAsset(a); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-[#EFEFEA] border-b border-border last:border-b-0 flex items-start gap-2"
                data-testid={`${testIdPrefix}-option-${a.id}`}
              >
                {isExact ? <Check size={12} className="text-[hsl(var(--positive))] mt-0.5" /> : <span className="w-3" />}
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold text-[13px] truncate">{a.investmentName}</div>
                  <div className="text-[10px] text-[#787872] font-mono truncate">
                    {a.mainAssetClass || '—'} · {a.subAssetClass || '—'} · {a.geography || '—'}
                    {a.institutions?.length > 0 && ` · in ${a.institutions.length} inst.`}
                  </div>
                </div>
              </button>
            );
          })}
          {canCreate && (
            <button
              type="button"
              onClick={() => { onCreateNew(query); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-[#E8F2EB] bg-[#F1F6F2] border-t border-border flex items-center gap-2 text-[hsl(var(--primary))]"
              data-testid={`${testIdPrefix}-create-new`}
            >
              <Plus size={13} />
              <span className="font-heading font-semibold text-[12px]">
                Create new asset: <span className="italic">"{query}"</span>
              </span>
            </button>
          )}
          {matches.length === 0 && !canCreate && (
            <div className="px-3 py-4 text-xs text-[#AAA]">Type at least 2 characters to create a new asset…</div>
          )}
        </div>
      )}
    </div>
  );
}
